<template>
  <div class="content task-binding-editor">
    <div class="header">
      <div class="title">
        <BaseTypeIcon :image="databaseIcon" size="xs" plain />
        <h4 class="text">{{ title }}</h4>
        <span class="subtitle">{{ database.data_name }}</span>
      </div>
      <BaseButton kind="auxiliary" class="close-icon" @click="$emit('close')">
        <template #icon><CloseIcon /></template>
      </BaseButton>
    </div>

    <div class="body">
      <FilterConfig
        v-if="panel === 'filter'"
        ref="filterEl"
        embedded
        :filter="binding.filter"
        :data-source="database"
        :available-fields="availableFields"
        @back="panel = null"
        @close="$emit('close')"
        @update:filter="saveFilter"
      />
      <StatusMappingConfig
        v-else-if="panel === 'status'"
        ref="statusEl"
        embedded
        :status-mapping="binding.status_mapping"
        :database="database"
        :status-options="statusOptions"
        :column-label="statusColumnLabel"
        @back="panel = null"
        @close="$emit('close')"
        @update:status-mapping="saveStatusMapping"
      />
      <ScheduledMappingConfig
        v-else-if="panel === 'scheduled'"
        ref="scheduledEl"
        embedded
        :scheduled-mapping="binding.scheduled_mapping"
        :database="database"
        :column-label="scheduledColumnLabel"
        @back="panel = null"
        @close="$emit('close')"
        @update:scheduled-mapping="saveScheduledMapping"
      />
      <UrlMappingConfig
        v-else-if="panel === 'url'"
        ref="urlEl"
        embedded
        :url-mapping="binding.url_mapping"
        :database="database"
        @back="panel = null"
        @close="$emit('close')"
        @update:url-mapping="saveUrlMapping"
      />
      <template v-else>
        <p class="description">
          Choose how rows of this data source become tasks in Routine.
        </p>
        <div class="parameters">
          <BaseButton
            v-if="shows('filter')"
            kind="auxiliary"
            text="Filter"
            is-expanded
            @click="openPanel('filter')"
          >
            <template #icon><FilterIcon /></template>
            <template #right>
              <span class="value" :class="{ unset: !filterLabel }">{{
                filterLabel ?? "None"
              }}</span>
              <ChevronRightIcon />
            </template>
          </BaseButton>
          <BaseButton
            v-if="shows('status')"
            kind="auxiliary"
            text="Completion"
            is-expanded
            @click="openPanel('status')"
          >
            <template #icon><CompleteIcon /></template>
            <template #right>
              <span class="value" :class="{ unset: !completionLabel }">{{
                completionLabel ?? "None"
              }}</span>
              <ChevronRightIcon />
            </template>
          </BaseButton>
          <BaseButton
            v-if="shows('scheduled')"
            kind="auxiliary"
            text="Planned for"
            is-expanded
            @click="openPanel('scheduled')"
          >
            <template #icon><ScheduleIcon /></template>
            <template #right>
              <span class="value" :class="{ unset: !plannedForLabel }">{{
                plannedForLabel ?? "None"
              }}</span>
              <ChevronRightIcon />
            </template>
          </BaseButton>
          <BaseButton
            v-if="shows('url')"
            kind="auxiliary"
            text="URL"
            is-expanded
            @click="openPanel('url')"
          >
            <template #icon><LinkIcon /></template>
            <template #right>
              <span class="value" :class="{ unset: !urlLabel }">{{
                urlLabel ?? "None"
              }}</span>
              <ChevronRightIcon />
            </template>
          </BaseButton>
        </div>
      </template>
    </div>

    <div class="footer">
      <div class="buttons">
        <template v-if="panel === null">
          <div class="buttons-left">
            <BaseButton
              v-if="canReset"
              kind="main"
              type="secondary"
              text="Reset"
              :state="resetting ? 'loading' : 'default'"
              @click="$emit('reset')"
            />
          </div>
          <div class="buttons-right">
            <BaseButton
              kind="main"
              type="primary"
              text="Done"
              @click="$emit('close')"
            />
          </div>
        </template>
        <template v-else>
          <div class="buttons-left">
            <BaseButton
              kind="main"
              type="tertiary"
              text="Back"
              @click="panel = null"
            />
            <BaseButton
              v-if="panelActions.canClear"
              kind="main"
              type="secondary"
              tint="red"
              :text="panelActions.clearLabel"
              @click="panelActions.clear"
            />
          </div>
          <div class="buttons-right">
            <BaseButton
              kind="main"
              type="primary"
              :text="panelActions.saveLabel"
              :state="panelActions.canSave ? 'default' : 'disabled'"
              @click="panelActions.save"
            />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, type PropType } from "vue";

import type {
  ColumnId,
  GraphDatabase,
  GraphViewWithHintsColumn,
  TaskBinding,
  TaskBindingConfig,
  TaskBindingStatusMapping,
  ViewFilterFormula,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import BaseTypeIcon from "@/components/base/BaseTypeIcon.vue";
import { getTypeIcon } from "@/components/databases";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import CompleteIcon from "@/components/icons/CompleteIcon.vue";
import FilterIcon from "@/components/icons/FilterIcon.vue";
import LinkIcon from "@/components/icons/LinkIcon.vue";
import ScheduleIcon from "@/components/icons/ScheduleIcon.vue";
import * as inject from "@/components/inject";
import ScheduledMappingConfig from "@/components/settings/ScheduledMappingConfig.vue";
import type { StatusValueOption } from "@/components/settings/status-value-mapping";
import StatusMappingConfig from "@/components/settings/StatusMappingConfig.vue";
import UrlMappingConfig from "@/components/settings/UrlMappingConfig.vue";
import FilterConfig from "@/components/views/config/FilterConfig.vue";

export type TaskBindingPanel = "filter" | "status" | "scheduled" | "url";

export function taskBindingConfig(binding: TaskBinding): TaskBindingConfig {
  const { rows: _rows, ...config } = binding;
  return config;
}

const panelTitles: Record<TaskBindingPanel, string> = {
  filter: "Filter",
  status: "Completion",
  scheduled: "Planned for",
  url: "URL",
};

/** What the modal footer offers while a mapping panel is open. */
interface PanelActions {
  saveLabel: string;
  clearLabel: string;
  canSave: boolean;
  canClear: boolean;
  save: () => void;
  clear: () => void;
}

const idleActions: PanelActions = {
  saveLabel: "Save",
  clearLabel: "Clear",
  canSave: false,
  canClear: false,
  save: () => {},
  clear: () => {},
};

/**
 * Standard modal content (header / body / footer) for customizing how a task
 * binding maps a data source onto tasks. Meant to be rendered as the direct
 * child of a `VueFinalModal`.
 */
export default defineComponent({
  name: "TaskBindingEditor",
  components: {
    BaseButton,
    BaseTypeIcon,
    ChevronRightIcon,
    CloseIcon,
    CompleteIcon,
    FilterConfig,
    FilterIcon,
    LinkIcon,
    ScheduleIcon,
    ScheduledMappingConfig,
    StatusMappingConfig,
    UrlMappingConfig,
  },
  props: {
    bindingId: { type: String, required: true },
    binding: { type: Object as PropType<TaskBinding>, required: true },
    database: { type: Object as PropType<GraphDatabase>, required: true },
    /** Mappings offered, in the order they are listed. */
    sections: {
      type: Array as PropType<TaskBindingPanel[]>,
      required: false,
      default: () => ["filter", "status", "scheduled", "url"],
    },
    statusOptions: {
      type: Array as PropType<StatusValueOption[]>,
      required: false,
      default: () => [],
    },
    statusColumnLabel: {
      type: String,
      required: false,
      default: "",
    },
    scheduledColumnLabel: {
      type: String,
      required: false,
      default: "",
    },
    /** Offer a "Reset" footer action (the caller owns what it resets to). */
    canReset: { type: Boolean, default: false },
    /** Shows the Reset action as loading while the caller's reset runs. */
    resetting: { type: Boolean, default: false },
  },
  emits: {
    close: () => true,
    reset: () => true,
  },
  setup(props) {
    const controller = inject.controller();
    const profile = inject.profile();
    const workspaceId = ref<WorkspaceId>(props.binding.workspace_id);
    inject.currentWorkspaceId(workspaceId);

    const panel = ref<TaskBindingPanel | null>(null);
    const availableFields = ref<Record<string, GraphViewWithHintsColumn>>({});

    const filterEl = ref<InstanceType<typeof FilterConfig>>();
    const statusEl = ref<InstanceType<typeof StatusMappingConfig>>();
    const scheduledEl = ref<InstanceType<typeof ScheduledMappingConfig>>();
    const urlEl = ref<InstanceType<typeof UrlMappingConfig>>();

    const databaseIcon = computed(() =>
      getTypeIcon(profile.value, props.database)
    );

    const title = computed(() =>
      panel.value === null ? "Customize" : panelTitles[panel.value]
    );

    const shows = (section: TaskBindingPanel) =>
      props.sections.includes(section);

    // The provider's own name for a column, when the caller knows it, beats the
    // name Routine sampled for the synced table.
    const columnLabel = (
      column: ColumnId | undefined,
      provided: string
    ): string | null =>
      column === undefined
        ? null
        : provided || props.database.type.fields[column]?.name || null;

    const filterLabel = computed(() =>
      props.binding.filter ? "Custom" : null
    );
    const completionLabel = computed(() =>
      columnLabel(props.binding.status_mapping?.column, props.statusColumnLabel)
    );
    const plannedForLabel = computed(() =>
      columnLabel(props.binding.scheduled_mapping, props.scheduledColumnLabel)
    );
    const urlLabel = computed(() => columnLabel(props.binding.url_mapping, ""));

    const openPanel = async (next: TaskBindingPanel) => {
      panel.value = next;
      if (next === "filter" && !Object.keys(availableFields.value).length) {
        const result = await controller.graph.view.ofQuery(
          props.binding.workspace_id,
          props.database.name
        );
        if ("value" in result) {
          availableFields.value = result.value.available_columns;
        }
      }
    };

    const upsert = async (patch: Partial<TaskBindingConfig>) => {
      await controller.task.binding.upsert(props.bindingId, {
        ...taskBindingConfig(props.binding),
        ...patch,
      });
    };

    const saveFilter = (filter: ViewFilterFormula | undefined) =>
      upsert({ filter });
    const saveStatusMapping = (
      status_mapping: TaskBindingStatusMapping | undefined
    ) => upsert({ status_mapping });
    const saveScheduledMapping = (scheduled_mapping: ColumnId | undefined) =>
      upsert({ scheduled_mapping });
    const saveUrlMapping = (url_mapping: ColumnId | undefined) =>
      upsert({ url_mapping });

    // The panels keep their draft and validation; the footer only drives them.
    // Clear is only offered while the panel shows a value to clear.
    const panelActions = computed<PanelActions>(() => {
      const hasColumn = (value: string | undefined) =>
        value !== undefined && value !== "";
      const byPanel: Record<TaskBindingPanel, () => PanelActions> = {
        filter: () => ({
          saveLabel: "Apply",
          clearLabel: "Delete filter",
          canSave: filterEl.value?.hasFilterChanges ?? false,
          canClear: filterEl.value?.localFilter !== undefined,
          save: () => filterEl.value?.apply(),
          clear: () => {
            filterEl.value?.deleteFilter();
            filterEl.value?.apply();
          },
        }),
        status: () => ({
          ...idleActions,
          canSave: statusEl.value?.canSave ?? false,
          canClear: hasColumn(statusEl.value?.selectedValue),
          save: () => statusEl.value?.save(),
          clear: () => statusEl.value?.clear(),
        }),
        scheduled: () => ({
          ...idleActions,
          canSave: scheduledEl.value?.canSave ?? false,
          canClear: hasColumn(scheduledEl.value?.selectedValue),
          save: () => scheduledEl.value?.save(),
          clear: () => scheduledEl.value?.clear(),
        }),
        url: () => ({
          ...idleActions,
          canSave: urlEl.value?.canSave ?? false,
          canClear: hasColumn(urlEl.value?.selectedValue),
          save: () => urlEl.value?.save(),
          clear: () => urlEl.value?.clear(),
        }),
      };
      return panel.value === null ? idleActions : byPanel[panel.value]();
    });

    return {
      panel,
      availableFields,
      filterEl,
      statusEl,
      scheduledEl,
      urlEl,
      databaseIcon,
      title,
      shows,
      filterLabel,
      completionLabel,
      plannedForLabel,
      urlLabel,
      openPanel,
      saveFilter,
      saveStatusMapping,
      saveScheduledMapping,
      saveUrlMapping,
      panelActions,
    };
  },
});
</script>

<style scoped lang="scss">
.task-binding-editor {
  // The shared modal rules (`.final-modal … > .content`) outrank scoped ones.
  width: 520px !important;
  min-width: 0 !important;
  max-width: calc(100vw - 32px) !important;

  > .body {
    display: flex;
    flex-direction: column;
    row-gap: 16px;
  }
}

.description {
  @include themed() {
    color: t($grey-50);
  }
}

.parameters {
  display: flex;
  flex-direction: column;
  row-gap: 2px;
  padding: 4px;
  border-radius: $border-radius-m;

  @include themed() {
    border: 1px solid t($grey-10);
  }

  button.base-button.isExpanded {
    height: 31px;
  }

  .value {
    @extend %font-caption-regular;

    @include themed() {
      color: t($grey-60);
    }

    &.unset {
      @include themed() {
        color: t($grey-40);
      }
    }
  }
}
</style>
